import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductOptionsModal from './ProductOptionsModal';
import { useCart } from '../../context/CartContext';
import { useCombos } from '../../context/CombosContext';

const ProductCard = ({ product, onAddToCart }) => {
    const [showOptions, setShowOptions] = useState(false);
    const [notificationState, setNotificationState] = useState('hidden');
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const { addItem } = useCart();
    const { hasComboGroupsSync, getComboGroups } = useCombos();
    const navigate = useNavigate();

    const handleAddToCart = async () => {
        if (isProcessing) return;
        setIsProcessing(true);

        try {
            const hasAttributes = product.custom_attributes?.length > 0;
            let hasCombos = hasComboGroupsSync(product.id);

            // If cache not yet loaded, fetch on demand
            if (hasCombos === null) {
                const result = await getComboGroups(product.id);
                hasCombos = result.has_combos;
            }

            if (hasAttributes || hasCombos) {
                setShowOptions(true);
            } else {
                addItem(product);
                navigate('/payment');
            }
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        if (notificationState === 'visible') {
            const timer = setTimeout(() => setNotificationState('exiting'), 1500);
            return () => clearTimeout(timer);
        } else if (notificationState === 'exiting') {
            const exitTimer = setTimeout(() => setNotificationState('hidden'), 300);
            return () => clearTimeout(exitTimer);
        }
    }, [notificationState]);

    const getMimeType = (b64) => b64?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

    const getImageSrc = () => {
        if (product.image_base64) {
            return `data:${getMimeType(product.image_base64)};base64,${product.image_base64}`;
        }
        if (product.id) {
            return `http://localhost:9000/images/products/${product.id}`;
        }
        return null;
    };

    const imageSrc = getImageSrc();

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700;900&display=swap');

                .notification-enter {
                    animation: slideInUp 300ms ease-out forwards;
                }
                .notification-exit {
                    animation: slideOutDown 300ms ease-in forwards;
                }
                .product-card-clean {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
                    border: 2px solid transparent;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    -webkit-tap-highlight-color: transparent;
                }
                .product-card-clean:hover {
                    border-color: #0E8164;
                    box-shadow: 0 8px 24px rgba(14,129,100,0.2);
                    transform: translateY(-4px);
                }
                .product-card-clean:active {
                    transform: scale(0.97);
                }
                .product-card-clean.is-processing {
                    opacity: 0.7;
                    pointer-events: none;
                }
                .card-clean-price {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    font-family: 'Montserrat', sans-serif;
                    font-weight: 900;
                    font-size: 1.5rem;
                    color: #0E8164;
                    background: white;
                    padding: 5px 12px;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
                    z-index: 2;
                    line-height: 1.3;
                }
                .card-clean-image {
                    width: 100%;
                    aspect-ratio: 1 / 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: white;
                    overflow: hidden;
                    padding: 0.75rem;
                }
                .card-clean-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    transition: transform 0.3s ease;
                }
                .product-card-clean:hover .card-clean-image img {
                    transform: scale(1.05);
                }
                .card-clean-name {
                    font-family: 'Montserrat', sans-serif;
                    font-weight: 700;
                    font-size: 1rem;
                    color: #0E8164;
                    text-align: center;
                    padding: 0 0.75rem 1rem;
                    line-height: 1.3;
                    background: white;
                }
                @keyframes slideInUp {
                    0% { opacity: 0; transform: translate(-50%, 20px) scale(0.8); }
                    100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
                }
                @keyframes slideOutDown {
                    0% { opacity: 1; transform: translate(-50%, 0) scale(1); }
                    100% { opacity: 0; transform: translate(-50%, 20px) scale(0.8); }
                }
            `}</style>

            <div
                className={`product-card-clean ${isProcessing ? 'is-processing' : ''}`}
                onClick={handleAddToCart}
            >
                <div className="card-clean-price">
                    ${product.price.toLocaleString()}
                </div>

                <div className="card-clean-image">
                    {imageSrc && !imageError ? (
                        <img
                            src={imageSrc}
                            alt={product.name}
                            style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                            onLoad={() => { setImageLoaded(true); setImageError(false); }}
                            onError={() => { setImageError(true); setImageLoaded(false); }}
                        />
                    ) : (
                        <svg style={{ width: '4rem', height: '4rem', color: '#ccc' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                    )}
                </div>

                <div className="card-clean-name">{product.name}</div>
            </div>

            {notificationState !== 'hidden' && (
                <div
                    className={`fixed bottom-20 left-1/2 z-50 px-6 py-3 rounded-xl shadow-2xl ${notificationState === 'visible' ? 'notification-enter' : 'notification-exit'}`}
                    style={{
                        background: 'linear-gradient(135deg, #0E8164 0%, #0a6b52 100%)',
                        color: 'white',
                        transform: 'translateX(-50%)'
                    }}
                >
                    <div className="flex items-center space-x-3 text-base font-semibold">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>¡Agregado al carrito!</span>
                    </div>
                </div>
            )}

            {showOptions && (
                <ProductOptionsModal
                    product={product}
                    isCombo={false}
                    onClose={() => setShowOptions(false)}
                    onSubmit={(selectedQuantities, totalPrice, attributesDescription, attribute_lines, drinkSelection) => {
                        const filteredAttributes = {};
                        if (product.custom_attributes?.length > 0) {
                            Object.entries(selectedQuantities).forEach(([attrId, qty]) => {
                                const attr = product.custom_attributes.find(a => a.id === parseInt(attrId));
                                const defaultQty = attr?.is_default ? attr.default_qty : 0;
                                const diff = qty - defaultQty;
                                if (diff !== 0) filteredAttributes[attrId] = diff;
                            });
                        }
                        const productOriginalId = String(product.id).split('-custom-')[0];
                        addItem({
                            ...product,
                            id: `${productOriginalId}-custom-${Date.now()}`,
                            original_id: productOriginalId,
                            price: totalPrice,
                            base_price: product.price,
                            customDescription: attributesDescription,
                            selectedAttributes: filteredAttributes,
                            attribute_lines: attribute_lines || [],
                            image_base64: product.image_base64,
                            hasOptions: true,
                            drinkSelection: drinkSelection || null
                        });
                        setShowOptions(false);
                        navigate('/payment');
                    }}
                />
            )}
        </>
    );
};

export default ProductCard;
